// Warning banner shown at the top of Home when an in-progress session has
// exceeded its `max_duration`. Dismissible per-session.

import { Icon } from '../Icon.tsx';
import { fmtDur } from '../../lib/format.ts';

export type OverdueItem = {
  kind: 'sleep' | 'breast' | 'pump';
  id: string;
  startedAt: number;
  needsReview?: boolean;
};

type Props = {
  items: OverdueItem[];
  onEnd: (item: OverdueItem) => void;
  onAdjust: (item: OverdueItem) => void;
  dismissed: Set<string>;
  onDismiss: (id: string) => void;
};

export function WarningBanner({ items, onEnd, onAdjust, dismissed, onDismiss }: Props) {
  const visible = items.filter((i) => !dismissed.has(i.id));
  if (visible.length === 0) return null;
  return (
    <div style={{ padding: '0 16px 12px' }}>
      {visible.map((it) => {
        const mins = Math.floor((Date.now() / 1000 - it.startedAt) / 60);
        const label =
          it.kind === 'sleep' ? 'Sleep' : it.kind === 'breast' ? 'Feed' : 'Pump';
        return (
          <div
            key={it.id}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '12px 14px',
              borderRadius: 14,
              background: 'var(--warn-soft)',
              border: '1px solid color-mix(in oklab, var(--warn) 30%, transparent)',
              marginBottom: 8,
            }}
          >
            <div style={{ color: 'var(--warn)', marginTop: 2 }}>
              <Icon.bell s={16} />
            </div>
            <div style={{ flex: 1, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.4 }}>
              <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>
                {label} has been running for {fmtDur(mins)}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button
                  type="button"
                  className="lt-btn lt-btn-soft"
                  style={{ padding: '6px 10px', fontSize: 12 }}
                  onClick={() => onDismiss(it.id)}
                >
                  Still going
                </button>
                <button
                  type="button"
                  className="lt-btn lt-btn-primary"
                  style={{ padding: '6px 10px', fontSize: 12 }}
                  onClick={() => onEnd(it)}
                >
                  {it.kind === 'pump' ? 'End + yield' : 'End now'}
                </button>
                <button
                  type="button"
                  className="lt-btn lt-btn-ghost"
                  style={{ padding: '6px 10px', fontSize: 12 }}
                  onClick={() => onAdjust(it)}
                >
                  Adjust
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
